import { Download, Share, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';

type BeforeInstallPromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };
const DISMISSED_KEY = 'smart-office-install-dismissed';
const isStandalone = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

// Only ever shown to a signed-in user inside the app — never on /login. An install nag on the
// public sign-in screen reads like a random website trying to install itself, not "your
// workspace is ready to pin to your home screen."
export function InstallPrompt() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosTip, setShowIosTip] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === '1');

  useEffect(() => {
    if (isStandalone() || dismissed || !user) return;
    const onPrompt = (event: Event) => { event.preventDefault(); setDeferred(event as BeforeInstallPromptEvent); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    if (isIOS()) setShowIosTip(true);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, [dismissed, user]);

  function dismiss() { localStorage.setItem(DISMISSED_KEY, '1'); setDismissed(true); }
  async function install() { if (!deferred) return; await deferred.prompt(); const { outcome } = await deferred.userChoice; setDeferred(null); if (outcome === 'accepted') dismiss(); }

  if (!user || dismissed || (!deferred && !showIosTip)) return null;
  return (
    <div className="fixed inset-x-4 bottom-24 z-40 mx-auto max-w-sm rounded-2xl border border-border bg-white p-4 shadow-card lg:bottom-6">
      <button aria-label="Dismiss" className="absolute right-3 top-3 text-muted hover:text-ink" onClick={dismiss}><X size={16} /></button>
      {deferred ? (
        <div className="flex items-start gap-3 pr-5">
          <Download className="mt-0.5 shrink-0 text-brand-600" size={20} />
          <div className="flex-1">
            <p className="font-semibold">Install Smart Office</p>
            <p className="mt-1 text-sm text-muted">Add it to your home screen for one-tap access.</p>
            <button className="btn-primary mt-3 w-full" onClick={() => void install()}>Install app</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-3 pr-5">
          <Share className="mt-0.5 shrink-0 text-brand-600" size={20} />
          <div>
            <p className="font-semibold">Add to Home Screen</p>
            <p className="mt-1 text-sm text-muted">Tap the Share icon in Safari, then "Add to Home Screen".</p>
          </div>
        </div>
      )}
    </div>
  );
}
